/**
 * PlannerPage
 *
 * Main production planner page where the manager reviews demand forecasts,
 * sees shadow batches suggested by the AI, and approves the production plan.
 *
 * US-C2-006: Planner Page UI
 * US-C2-007: Batch Approval UI
 * US-C2-009: Prediction Accuracy Panel
 * US-C2-011: Timeline <-> Planner bidirectional navigation
 * Strain Families: filter by family instead of individual strains
 */

import { useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import PageLayout from '@/components/layout/PageLayout'
import PlannerGrid from '@/components/planner/PlannerGrid'
import PredictionAccuracyPanel from '@/components/planner/PredictionAccuracyPanel'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useStrainFamilies } from '@/hooks/useStrains'
import { RefreshCw, Calendar, ArrowLeft } from 'lucide-react'

const WEEK_COUNT_OPTIONS = [
  { value: 4, label: '4 weeks' },
  { value: 8, label: '8 weeks' },
  { value: 12, label: '12 weeks' }
]

export default function PlannerPage({ user, onLogout }) {
  const [searchParams] = useSearchParams()
  const highlightWeek = searchParams.get('week')

  const [weekCount, setWeekCount] = useState(8)
  const [familyFilter, setFamilyFilter] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const { families } = useStrainFamilies()

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1)
  }

  return (
    <PageLayout
      title="Production Planner"
      subtitle="Review demand forecasts and approve shadow batches"
      user={user}
      onLogout={onLogout}
      headerActions={
        <div className="flex items-center gap-3">
          {/* Family Filter */}
          <Select
            value={familyFilter || 'all'}
            onValueChange={(value) => setFamilyFilter(value === 'all' ? null : value)}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All families" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All families</SelectItem>
              {families.map(family => (
                <SelectItem key={family.id} value={family.id}>
                  {family.name} ({family.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Week Range */}
          <Select
            value={weekCount.toString()}
            onValueChange={(value) => setWeekCount(parseInt(value, 10))}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WEEK_COUNT_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {option.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Refresh */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* US-C2-011: Back to Timeline link when navigated from timeline */}
        {highlightWeek && (
          <div className="flex items-center gap-2">
            <Link
              to="/production/timeline"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Timeline
            </Link>
            <span className="text-sm text-muted-foreground">
              Showing week of {new Date(highlightWeek + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
        )}

        {/* Planner Grid */}
        <PlannerGrid
          key={refreshKey}
          weekCount={weekCount}
          familyFilter={familyFilter}
          highlightWeek={highlightWeek}
        />

        {/* Prediction Accuracy Panel (collapsible) */}
        <PredictionAccuracyPanel />
      </div>
    </PageLayout>
  )
}
